import { KanbanColumn } from '@/components/pipeline/KanbanColumn';
import { StatTile } from '@/components/pipeline/StatTile';
import { SubmissionCard } from '@/components/pipeline/SubmissionCard';

// ---------------------------------------------------------------------------
// Dashboard — Pipeline overview with stat tiles, filters, and kanban
// ---------------------------------------------------------------------------

interface CardData {
  id: string;
  businessName: string;
  linesOfCoverage: string[];
  daysInStage: number;
  completeness: number;
  assignedAgent?: { name: string };
}

// Placeholder data (wired to API later)
const STATS: Array<{
  label: string;
  value: string;
  accent: 'blue' | 'orange' | 'green' | 'slate';
  trend?: { direction: 'up' | 'down' | 'flat'; pct?: number };
}> = [
  { label: 'New Today', value: '12', accent: 'blue', trend: { direction: 'up', pct: 25 } },
  { label: 'Pending Info', value: '28', accent: 'orange', trend: { direction: 'down', pct: 15 } },
  { label: 'Ready to Market', value: '15', accent: 'blue', trend: { direction: 'up', pct: 13 } },
  { label: 'Quoted (MTD)', value: '9', accent: 'slate', trend: { direction: 'flat' } },
  { label: 'Bound (MTD)', value: '42', accent: 'green', trend: { direction: 'up', pct: 19 } },
  { label: 'Conversion Rate', value: '34%', accent: 'blue', trend: { direction: 'up', pct: 2 } },
];

const COLUMNS: Array<{
  status: string;
  label: string;
  accent: 'blue' | 'orange' | 'amber' | 'emerald' | 'red';
  cards: CardData[];
}> = [
  {
    status: 'new',
    label: 'New',
    accent: 'blue',
    cards: [
      {
        id: '1',
        businessName: 'ABC Plumbing LLC',
        linesOfCoverage: ['gl_bop', 'wc', 'auto'],
        daysInStage: 1,
        completeness: 65,
        assignedAgent: { name: 'Sarah Jones' },
      },
      {
        id: '2',
        businessName: "Joe's Auto Repair",
        linesOfCoverage: ['gl_bop', 'auto'],
        daysInStage: 0,
        completeness: 40,
      },
    ],
  },
  {
    status: 'in_review',
    label: 'In Review',
    accent: 'amber',
    cards: [
      {
        id: '3',
        businessName: 'Skyline Construction',
        linesOfCoverage: ['gl_bop', 'wc', 'auto', 'umbrella'],
        daysInStage: 2,
        completeness: 85,
        assignedAgent: { name: 'Mike Chen' },
      },
    ],
  },
  {
    status: 'pending_info',
    label: 'Pending Info',
    accent: 'orange',
    cards: [
      {
        id: '4',
        businessName: "Mama Mia's Restaurant",
        linesOfCoverage: ['gl_bop', 'wc'],
        daysInStage: 5,
        completeness: 70,
        assignedAgent: { name: 'Sarah Jones' },
      },
      {
        id: '5',
        businessName: 'Apex Roofing Co',
        linesOfCoverage: ['gl_bop', 'wc'],
        daysInStage: 4,
        completeness: 55,
        assignedAgent: { name: 'Mike Chen' },
      },
    ],
  },
  {
    status: 'ready_to_market',
    label: 'Ready to Market',
    accent: 'emerald',
    cards: [
      {
        id: '6',
        businessName: 'Downtown Property Mgmt',
        linesOfCoverage: ['gl_bop', 'prof_cyber'],
        daysInStage: 1,
        completeness: 100,
        assignedAgent: { name: 'Sarah Jones' },
      },
    ],
  },
  {
    status: 'quoted',
    label: 'Quoted',
    accent: 'blue',
    cards: [
      {
        id: '7',
        businessName: 'Greenleaf Landscaping',
        linesOfCoverage: ['gl_bop', 'wc', 'auto'],
        daysInStage: 2,
        completeness: 100,
        assignedAgent: { name: 'Mike Chen' },
      },
    ],
  },
];

export default function DashboardPage() {
  return (
    <div className="min-h-screen bg-slate-50">
      <div className="border-b border-slate-200 bg-white">
        <div className="max-w-screen-2xl mx-auto px-6 py-5">
          <h1 className="text-2xl font-semibold text-slate-900">Pipeline</h1>
          <p className="text-sm text-slate-600 mt-1">Live view of every active submission</p>
        </div>
      </div>

      <div className="max-w-screen-2xl mx-auto px-6 py-6 space-y-6">
        {/* Stat tiles */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {STATS.map((s) => (
            <StatTile key={s.label} {...s} />
          ))}
        </div>

        {/* Filters */}
        <div className="flex items-center gap-3 bg-white border border-slate-200 rounded-lg p-3">
          <select className="text-sm rounded-md border border-slate-300 px-3 py-1.5">
            <option>All agents</option>
            <option>Sarah Jones</option>
            <option>Mike Chen</option>
          </select>
          <select className="text-sm rounded-md border border-slate-300 px-3 py-1.5">
            <option>All lines</option>
            <option>GL/BOP</option>
            <option>Workers Comp</option>
            <option>Commercial Auto</option>
            <option>Professional / E&O</option>
          </select>
          <input
            type="search"
            placeholder="Search submissions..."
            className="flex-1 text-sm rounded-md border border-slate-300 px-3 py-1.5"
          />
          <button
            type="button"
            className="text-sm rounded-md bg-blue-600 text-white px-4 py-1.5 hover:bg-blue-700"
          >
            + New submission
          </button>
        </div>

        {/* Kanban */}
        <div className="flex gap-4 overflow-x-auto pb-4">
          {COLUMNS.map((col) => (
            <KanbanColumn
              key={col.status}
              label={col.label}
              count={col.cards.length}
              accent={col.accent}
            >
              {col.cards.map((card) => (
                <SubmissionCard
                  key={card.id}
                  id={card.id}
                  businessName={card.businessName}
                  linesOfCoverage={card.linesOfCoverage}
                  daysInStage={card.daysInStage}
                  completeness={card.completeness}
                  assignedAgent={card.assignedAgent}
                />
              ))}
            </KanbanColumn>
          ))}
        </div>
      </div>
    </div>
  );
}
